import { useState, useCallback, useMemo } from "react";
import { Draft } from "@/shared/contexts/DraftContext/DraftContext";

export const valuesAreEqual = (value1: unknown, value2: unknown): boolean => {
  if (value1 === value2) return true;

  const isNullish1 = value1 === null || value1 === undefined;
  const isNullish2 = value2 === null || value2 === undefined;

  if (isNullish1 || isNullish2) {
    if (isNullish1 && isNullish2) return true;
    const nonNullish = isNullish1 ? value2 : value1;
    if (nonNullish === "") return true;
    return false;
  }

  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime();
  }

  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false;
    return value1.every((item, index) => valuesAreEqual(item, value2[index]));
  }

  if (typeof value1 === "object" && typeof value2 === "object" && value1 !== null && value2 !== null) {
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every((key) => {
      const val1 = (value1 as Record<string, unknown>)[key];
      const val2 = (value2 as Record<string, unknown>)[key];
      return valuesAreEqual(val1, val2);
    });
  }

  return value1 === value2;
};

export const useDraftProvider = () => {
  const [drafts, setDrafts] = useState<Record<string, Record<string, Draft>>>({});

  const initializeDraft = useCallback(<T>(entityType: string, entityId: string | null, originalData: Partial<T>) => {
    const key = entityId || "new";
    // Deep clone to prevent mutation from the source object (e.g. RHF internal store)
    const clonedOriginalData = structuredClone(originalData);

    setDrafts((prev) => {
      const entityDrafts = prev[entityType] || {};
      const existingDraft = entityDrafts[key];

      if (!existingDraft?.originalData || Object.keys(existingDraft.originalData).length === 0) {
        return {
          ...prev,
          [entityType]: {
            ...entityDrafts,
            [key]: {
              id: entityId,
              formData: existingDraft?.formData || {},
              originalData: clonedOriginalData,
              changedFields: existingDraft?.changedFields || [],
              lastModified: Date.now(),
            },
          },
        };
      }

      return prev;
    });
  }, []);

  const saveDraft = useCallback(<T>(entityType: string, entityId: string | null, formData: Partial<T>, changedFields: string[]) => {
    const key = entityId || "new";

    setDrafts((prev) => {
      const entityDrafts = prev[entityType] || {};
      const currentDraft = entityDrafts[key];

      return {
        ...prev,
        [entityType]: {
          ...entityDrafts,
          [key]: {
            id: entityId,
            formData,
            originalData: currentDraft?.originalData || {},
            changedFields,
            lastModified: Date.now(),
          },
        },
      };
    });
  }, []);

  const updateDraftField = useCallback((entityType: string, entityId: string | null, fieldName: string, fieldValue: unknown) => {
    const key = entityId || "new";

    setDrafts((prev) => {
      const entityDrafts = prev[entityType] || {};
      const currentDraft = entityDrafts[key];

      // Skip if draft hasn't been initialized yet
      if (!currentDraft || Object.keys(currentDraft.originalData).length === 0) {
        return prev;
      }

      const updatedFormData = {
        ...currentDraft.formData,
        [fieldName]: fieldValue,
      };

      const originalValue = currentDraft.originalData[fieldName];
      const isValueChanged = !valuesAreEqual(fieldValue, originalValue);

      let updatedChangedFields = [...currentDraft.changedFields];

      if (isValueChanged) {
        if (!updatedChangedFields.includes(fieldName)) {
          updatedChangedFields.push(fieldName);
        }
      }
      else {
        updatedChangedFields = updatedChangedFields.filter((field) => field !== fieldName);
      }

      return {
        ...prev,
        [entityType]: {
          ...entityDrafts,
          [key]: {
            ...currentDraft,
            formData: updatedFormData,
            changedFields: updatedChangedFields,
            lastModified: Date.now(),
          },
        },
      };
    });
  }, []);

  const removeDraft = useCallback((entityType: string, entityId: string | null) => {
    const key = entityId || "new";

    setDrafts((prev) => {
      const entityDrafts = prev[entityType] || {};
      const { [key]: removed, ...remainingDrafts } = entityDrafts;

      return {
        ...prev,
        [entityType]: remainingDrafts,
      };
    });
  }, []);

  const getDraft = useCallback(<T>(entityType: string, entityId: string | null): Draft<T> | null => {
    const key = entityId || "new";
    return (drafts[entityType]?.[key] as Draft<T>) || null;
  }, [drafts]);

  const hasDraft = useCallback((entityType: string, entityId: string | null): boolean => {
    const key = entityId || "new";
    const draft = drafts[entityType]?.[key];
    return Boolean(draft && draft.changedFields.length > 0);
  }, [drafts]);

  const getChangedFields = useCallback((entityType: string, entityId: string | null): string[] => {
    const draft = getDraft(entityType, entityId);
    return draft?.changedFields || [];
  }, [getDraft]);

  const clearAllDrafts = useCallback(() => {
    setDrafts({});
  }, []);

  return useMemo(() => ({
    initializeDraft,
    saveDraft,
    updateDraftField,
    removeDraft,
    getDraft,
    hasDraft,
    getChangedFields,
    clearAllDrafts,
  }), [initializeDraft, saveDraft, updateDraftField, removeDraft, getDraft, hasDraft, getChangedFields, clearAllDrafts]);
};
